import { useEffect, useState } from 'react';

import { fetchQuery, graphql, type Environment } from 'react-relay';
import { createOperationDescriptor, getRequest } from 'relay-runtime';

import { useGoalWorkspace, useProjectWorkspace } from '@townsquare/collab-context';
import { filterNull } from '@townsquare/filter-type';
import { useOrgContextStore } from '@townsquare/org-context';
import { getRelayEnvironment } from '@townsquare/relay-utils/townsquare-environment';
import { UserCapabilityType } from '@townsquare/router/types';

import {
	type useUserHasCapabilitiesQuery as useUserHasCapabilitiesQueryType,
	default as useUserHasCapabilitiesQuery,
	type useUserHasCapabilitiesQuery$data,
} from './__generated__/useUserHasCapabilitiesQuery.graphql';
import { checkUserHasCapabilities } from './utils';

const getWorkspaceFromBootstrap = (
	bootstrapData: useUserHasCapabilitiesQuery$data['bootstrap'],
	cloudId: string | null,
) => {
	if (!bootstrapData || !bootstrapData.workspaces || !cloudId) {
		return null;
	}

	return bootstrapData.workspaces
		.filter(filterNull)
		.find((workspace) => workspace.cloudId === cloudId);
};

const getUserHasCapabilities = async (
	requirements: UserCapabilityType[],
	allCapabilitiesRequired: boolean,
	environment: Environment,
	cloudId: string,
	orgId: string,
) => {
	try {
		const data = await fetchQuery<useUserHasCapabilitiesQueryType>(
			environment,
			graphql`
				query useUserHasCapabilitiesQuery(
					$context: OrganizationContextInput!
					$isValidWorkspace: Boolean!
				) {
					bootstrap(organizationContext: $context) @include(if: $isValidWorkspace) {
						workspaces {
							cloudId
							...utils_checkUserHasCapabilities
						}
					}
				}
			`,
			{
				isValidWorkspace: true,
				context: {
					cloudId,
					orgId,
				},
			},
			{
				fetchPolicy: 'store-or-network',
			},
		).toPromise();

		if (!data) {
			return false;
		}

		const workspace = getWorkspaceFromBootstrap(data.bootstrap, cloudId);

		return checkUserHasCapabilities(workspace, requirements, allCapabilitiesRequired);
	} catch (e) {
		return false;
	}
};

/**
 * This hook checks if the user has the capabilities that are passed in.
 * @param requirements - required capabilities
 * @param allCapabilitiesRequired - true if all capabilities in requirements are necessary, false if at least one is required
 * @returns true if user has capabilities, otherwise returns false (boolean)
 */
export const useUserHasCapabilities = (
	requirements: UserCapabilityType[],
	allCapabilitiesRequired: boolean,
): any => {
	// Capability data only exists on the Status (TownSquare) GraphQL schema and is prefetched into the
	// Status Relay store by Bootstrap. Hard-wire to the Status environment so AGG-wrapped consumers
	// (e.g. `useRelayEnvironment()` returning the AGG environment) still hit the prefetched cache.
	const environment = getRelayEnvironment();
	const goalWorkspace = useGoalWorkspace();
	const projectWorkspace = useProjectWorkspace();
	const workspaceCloudId = goalWorkspace ?? projectWorkspace;
	const [{ orgId }] = useOrgContextStore();

	// `useClientQuery` reads its environment from React context, so it cannot target the Status store
	// from inside an AGG provider. Use `environment.lookup` to read the Status store directly.
	const operation = createOperationDescriptor(getRequest(useUserHasCapabilitiesQuery), {
		isValidWorkspace: Boolean(workspaceCloudId && orgId),
		context: {
			cloudId: workspaceCloudId,
			orgId,
		},
	});
	const snapshot = environment.lookup(operation.fragment);
	const data = snapshot.data as useUserHasCapabilitiesQuery$data | null | undefined;
	const workspace = getWorkspaceFromBootstrap(data?.bootstrap ?? null, workspaceCloudId);

	const [hasCapabilities, setHasCapabilities] = useState(
		checkUserHasCapabilities(workspace, requirements, allCapabilitiesRequired),
	);

	useEffect(() => {
		if (workspaceCloudId) {
			getUserHasCapabilities(
				requirements,
				allCapabilitiesRequired,
				environment,
				workspaceCloudId,
				orgId,
			).then(setHasCapabilities);
		}
	}, [environment, requirements, allCapabilitiesRequired, workspaceCloudId, orgId]);

	if (!workspaceCloudId) {
		return false;
	} else {
		return hasCapabilities;
	}
};
